import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import SidePanel from '../../components/SidePanel/SidePanel';
import { apiList, verifyLogin } from '../../helpers';

// Photo location check for one loan: where each photo was taken vs the
// property and the radius allowed when it was taken (800 m by default;
// photos taken with geo-tagging off have no radius).

const IMAGE = /\.(jpe?g|png|gif|webp|heic)(\?|$)/i;
const COLORS = { in: '#12B76A', out: '#D92D20', na: '#98A2B3' };

const kindOf = (p) => {
  if (p.radius === null || p.distance === null) return 'na';
  return p.outside ? 'out' : 'in';
};

const metres = (m) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`);

export default function PhotoLocationsPanel({ loan, onClose }) {
  const open = Boolean(loan);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [only, setOnly] = useState('all');
  const el = useRef(null);
  const map = useRef(null);
  const pins = useRef({});

  useEffect(() => {
    if (!loan) return undefined;
    let active = true;
    setData(null);
    setError('');
    setOnly('all');
    const { session } = verifyLogin();
    axios.get(`${apiList.MAP_LOANS}/${loan.loanId}/photos`, {
      headers: { session, permissionConfig: 'Property Details', feature: 'view' },
    })
      .then((r) => { if (active) setData(r.data.data); })
      .catch(() => { if (active) setError('Could not load the photos for this loan.'); });
    return () => { active = false; };
  }, [loan]);

  // Small map: property, allowed radius, one dot per photo.
  useEffect(() => {
    const { google } = window;
    if (!google || !data || !el.current) return;
    map.current = new google.maps.Map(el.current, {
      mapTypeControl: false, streetViewControl: false, clickableIcons: false,
    });
    pins.current = {};
    const bounds = new google.maps.LatLngBounds();
    if (data.property) {
      pins.current.property = new google.maps.Marker({
        map: map.current, position: data.property, title: 'Property', zIndex: 10,
      });
      bounds.extend(data.property);
      const radii = data.photos.map((p) => p.radius).filter(Boolean);
      const radius = radii.length ? radii.sort((a, b) => radii.filter((r) => r === b).length
        - radii.filter((r) => r === a).length)[0] : null;
      if (radius) {
        const circle = new google.maps.Circle({
          map: map.current,
          center: data.property,
          radius,
          strokeColor: '#2F6BEA',
          strokeOpacity: 0.8,
          strokeWeight: 1.5,
          fillColor: '#2F6BEA',
          fillOpacity: 0.08,
          clickable: false,
        });
        bounds.union(circle.getBounds());
      }
    }
    data.photos.filter((p) => p.lat !== null).forEach((p) => {
      const kind = kindOf(p);
      pins.current[p.id] = new google.maps.Marker({
        map: map.current,
        position: { lat: p.lat, lng: p.lng },
        title: `${p.activity} · ${p.submittedAt}${p.distance !== null ? ` · ${metres(p.distance)}` : ''}`,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: COLORS[kind],
          fillOpacity: 0.9,
          strokeColor: '#fff',
          strokeWeight: 1.5,
          scale: 6,
        },
      });
      bounds.extend({ lat: p.lat, lng: p.lng });
    });
    if (!bounds.isEmpty()) map.current.fitBounds(bounds, 30);
  }, [data]);

  const focus = (p) => {
    if (!map.current || p.lat === null) return;
    map.current.panTo({ lat: p.lat, lng: p.lng });
    if (map.current.getZoom() < 18) map.current.setZoom(18);
    const pin = pins.current[p.id];
    if (pin) {
      pin.setAnimation(window.google.maps.Animation.BOUNCE);
      setTimeout(() => pin.setAnimation(null), 1400);
    }
  };

  const photos = data ? data.photos : [];
  const outside = photos.filter((p) => p.outside).length;
  const shown = only === 'outside' ? photos.filter((p) => p.outside) : photos;

  return (
    <SidePanel
      open={open}
      setOpen={(value) => { if (!value) onClose(); }}
      title={`Photo locations${loan ? ` · ${loan.loanNumber || ''}` : ''}`}
      subtitle='Where each photo was taken, compared with the property and the radius allowed at the time.'
      width={960}
      showAction={false}
      body={(
        <>
          {error ? <div className='lm-alert'>{error}</div> : null}
          {!data && !error ? <div className='lm-summary'>Loading…</div> : null}
          {data ? (
            <>
              {!data.property ? (
                <div className='lm-alert'>This property has no valid location, so distances can&apos;t be checked.</div>
              ) : null}
              <div className='lm-photo-map'><div ref={el} className='lm-map-canvas' /></div>
              <div className='lm-photo-summary'>
                <span>
                  <strong>{photos.length}</strong>
                  {photos.length === data.limit ? ' most recent photos' : ' photos'}
                </span>
                <span style={{ color: COLORS.out }}>
                  <strong style={{ color: COLORS.out }}>{outside}</strong>
                  {' outside the radius'}
                </span>
                <ToggleButtonGroup
                  className='home-range'
                  size='small'
                  exclusive
                  value={only}
                  onChange={(e, value) => { if (value) setOnly(value); }}
                >
                  <ToggleButton value='all'>All</ToggleButton>
                  <ToggleButton value='outside'>Outside radius</ToggleButton>
                </ToggleButtonGroup>
              </div>
              {shown.length ? shown.map((p) => {
                const kind = kindOf(p);
                return (
                  <div
                    key={p.id}
                    className='lm-photo'
                    role='button'
                    tabIndex={0}
                    onClick={() => focus(p)}
                    onKeyPress={() => focus(p)}
                  >
                    <a
                      className='lm-thumb'
                      href={p.url}
                      target='_blank'
                      rel='noreferrer'
                      onClick={(e) => e.stopPropagation()}
                      style={IMAGE.test(p.url || '') ? { backgroundImage: `url("${p.url}")` } : undefined}
                    >
                      {IMAGE.test(p.url || '') ? null : 'Video'}
                    </a>
                    <div className='lm-photo-main'>
                      <div className='t'>{p.activity || 'Activity'}</div>
                      <div className='s'>
                        {[p.location, p.submittedAt, p.submittedBy, p.rejected ? 'rejected' : p.status]
                          .filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <span className={`lm-distance ${kind}`}>
                      {kind === 'na' ? 'No radius / location' : `${metres(p.distance)}${kind === 'out' ? ` (limit ${metres(p.radius)})` : ''}`}
                    </span>
                  </div>
                );
              }) : <div className='lm-empty'>No photos here.</div>}
            </>
          ) : null}
        </>
      )}
    />
  );
}
